import {KeyboardArrowDown} from "@mui/icons-material"
import {Box, Collapse, Typography} from "@mui/material"
import {memo, PropsWithChildren, ReactNode, useState} from "react"

import {SxPropsMap} from "../../helper/HelperType"
import {Hint} from "./Hint"

const SX: SxPropsMap = {
    box: {display: "flex", flexDirection: "column"},
    island: {padding: "6px 8px", border: 1, borderColor: "divider", borderRadius: 2},
    head: {position: "relative", display: "flex", alignItems: "center", gap: 1, padding: "3px 6px"},
    hint: {padding: "0px 6px"},
    label: {flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", userSelect: "none", gap: 0.5},
    labelToggle: {cursor: "pointer", "&:hover": {color: "primary.main"}, "&:hover *": {color: "primary.main"}},
    actions: {
        position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)",
        display: "flex", alignItems: "center", flexDirection: "column", gap: 0.5,
    },
    icon: {fontSize: "20px", transition: "transform 0.2s", transform: "rotate(-90deg)"},
    iconDense: {fontSize: "16px"},
    iconOpen: {transform: "rotate(0deg)"},
    text: {
        fontSize: "15px", fontWeight: 600, textTransform: "uppercase", color: "text.secondary",
        fontFamily: "monospace", transition: "color 0.2s", lineHeight: 1,
    },
    textDense: {fontSize: "13px"},
    gap: {display: "flex", gap: 0.5, flexDirection: "column", marginTop: 0.5},
}

type Props = {
    label: string,
    renderActions?: ReactNode,
    defaultOpen?: boolean,
    open?: boolean,
    onOpenChange?: (open: boolean) => void,
    island?: boolean,
    dense?: boolean,
    hint?: string,
    collapsible?: boolean,
}

export const TitleBox = memo(function TitleBox(props: PropsWithChildren<Props>) {
    const {label, hint, children, renderActions, defaultOpen = false, island = false, dense = false} = props
    const {collapsible = true, onOpenChange} = props
    const [uncontrolled, setUncontrolled] = useState(defaultOpen)
    const open = props.open ?? uncontrolled

    return (
        <Box sx={[SX.box, island && SX.island]}>
            <Box sx={[SX.head, dense && SX.headDense]}>
                <Box sx={[SX.label, collapsible && SX.labelToggle]} onClick={collapsible ? handleToggle : undefined}>
                    <Typography sx={[SX.text, dense && SX.textDense]}>{label}</Typography>
                    {collapsible && renderIcon()}
                </Box>
                {renderActions && <Box sx={SX.actions}>{renderActions}</Box>}
            </Box>
            {renderContent()}
        </Box>
    )

    function renderIcon() {
        return <KeyboardArrowDown sx={[SX.icon, dense && SX.iconDense, open && SX.iconOpen]}/>
    }

    function renderContent() {
        if (!hint && !children) return
        if (!collapsible) return renderContentBody()
        return <Collapse in={open} mountOnEnter={true}>{renderContentBody()}</Collapse>
    }

    function renderContentBody() {
        return (
            <Box sx={SX.gap}>
                {hint && <Hint sx={SX.hint}>{hint}</Hint>}
                {children && <Box>{children}</Box>}
            </Box>
        )
    }

    function handleToggle() {
        setUncontrolled(!open)
        onOpenChange?.(!open)
    }
})
